import React, { useState } from 'react';
import { User } from 'lucide-react';

interface AvatarProps {
  src?: string | null;
  alt?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  fallbackIcon?: React.ReactNode;
  onError?: () => void;
}

const Avatar: React.FC<AvatarProps> = ({ 
  src, 
  alt = '프로필', 
  size = 'md', 
  className = '', 
  fallbackIcon,
  onError 
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // 아바타 URL을 절대 경로로 변환하는 함수
  const getAvatarUrl = (avatarPath: string | undefined | null) => {
    if (!avatarPath) return null;
    if (avatarPath.startsWith('http')) return avatarPath;
    // 레거시 경로 호환 처리
    const normalized = avatarPath.replace(/^\/api\/uploads\//, '/uploads/');
    // API_URL에서 /api 제거 (정적 파일은 직접 서빙되므로)
    let API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
    API_URL = API_URL.replace(/\/api$/, ''); // /api 제거
    return `${API_URL}${normalized}`;
  };

  const handleImageError = () => {
    console.error('❌ 아바타 이미지 로드 실패:', src);
    console.log('🔍 이미지 URL:', avatarUrl);
    console.log('🔍 API URL:', import.meta.env.VITE_API_URL);
    setImageError(true);
    setIsLoading(false);
    onError?.();
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  // 크기별 스타일 설정
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8 sm:w-10 sm:h-10',
    lg: 'w-16 h-16',
    xl: 'w-20 h-20'
  };

  const iconSizes = {
    sm: 12,
    md: 14,
    lg: 24,
    xl: 32
  };

  const avatarUrl = getAvatarUrl(src);
  const shouldShowImage = avatarUrl && !imageError;

  return (
    <div className={`${sizeClasses[size]} ${className} relative`}>
      {/* 로딩 상태 */}
      {isLoading && shouldShowImage && (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-600 to-slate-800 rounded-full flex items-center justify-center">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
        </div>
      )}
      
      {/* 이미지 또는 기본 아이콘 */}
      {shouldShowImage ? (
        <img 
          src={avatarUrl} 
          alt={alt}
          className={`w-full h-full rounded-full object-cover ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}
          onError={handleImageError}
          onLoad={handleImageLoad}
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-slate-600 to-slate-800 rounded-full flex items-center justify-center">
          {fallbackIcon || <User className="text-white" size={iconSizes[size]} />}
        </div>
      )}
    </div>
  );
};

export default Avatar;
